import React, { useEffect, useState, useCallback, useRef } from "react";
import { Form, Pagination } from "react-bootstrap";
import * as XLSX from "xlsx";
import { API_BASE_URL } from "../../utils/constants";
import { useAuth } from "../../components/AuthProvider";
import { FaCheckCircle, FaTimesCircle, FaFileExcel, FaFilePdf, FaExternalLinkAlt } from "react-icons/fa";

const STAGES = [
  { key: "Uploaded_Date", label: "Uploaded", docKey: null },
  { key: "Appointment_Letter", label: "Appt Letter", docKey: "Appointment_Letter_Doc" },
  { key: "Acceptance_Letter", label: "Acceptance", docKey: "Acceptance_Letter_Doc" },
  { key: "SOC", label: "SOC", docKey: "SOC_Doc" },
  { key: "Sec17_Application", label: "Sec17 App", docKey: "Sec17_Application_Doc" },
  { key: "Sec17_Order", label: "Sec17 Order", docKey: "Sec17_Order_Doc" },
  { key: "First_Hearing", label: "1st Hearing", docKey: null },
  { key: null, label: "1st Roznama", docKey: "First_Hearing_Roznama_Doc" },
  { key: null, label: "2nd Notice", docKey: "Second_Hearing_Notice_Doc" },
  { key: "Second_Hearing", label: "2nd Hearing", docKey: null },
  { key: null, label: "2nd Roznama", docKey: "Second_Hearing_Roznama_Doc" },
  { key: null, label: "3rd Notice", docKey: "Third_Hearing_Notice_Doc" },
  { key: "Third_Hearing", label: "3rd Hearing", docKey: null },
  { key: null, label: "3rd Roznama", docKey: "Third_Hearing_Roznama_Doc" },
  { key: null, label: "PURSIS", docKey: "PURSIS_Doc" },
  { key: "Award", label: "Award", docKey: "Award_Doc" },
];

const fmt = (d) => {
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return null;
  return `${String(dt.getMonth() + 1).padStart(2, "0")}/${String(dt.getDate()).padStart(2, "0")}/${dt.getFullYear()}`;
};

const statusBadge = (status) => {
  const map = {
    Active: "badge bg-success",
    "On Hold": "badge bg-warning text-dark",
    Terminated: "badge bg-danger",
  };
  return <span className={map[status] || "badge bg-secondary"}>{status}</span>;
};

const handleDownload = async (serviceId, serviceTypeId, label) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/CaseFile?C=${serviceId}&S=${serviceTypeId}`,
      { method: "GET" }
    );

    if (!response.ok) {
      throw new Error("Failed to download document");
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${label}_${serviceId}.pdf`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  } catch (error) {
    console.error("Download error:", error);
    alert("Failed to download document");
  }
};

const MisSheet = ({ mode = "resolution" }) => {
  const { user } = useAuth();
  const arbIdFromUser = user?.[0]?.Ref_id;

  const [arbitrators, setArbitrators] = useState([]);
  const [filterArbId, setFilterArbId] = useState("");
  const [filterLotNo, setFilterLotNo] = useState("");
  const [filterAccountNo, setFilterAccountNo] = useState("");
  const [filterRefNo, setFilterRefNo] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const PER_PAGE = 15;

  const scrollPosRef = useRef(0);
  const shouldRestoreScroll = useRef(false);

  useEffect(() => {
    if (mode !== "resolution") return;
    fetch(`${API_BASE_URL}/api/ArbListWithCaseCount`)
      .then((r) => r.json())
      .then((res) => setArbitrators(Array.isArray(res) ? res : []))
      .catch(console.error);
  }, [mode]);

  // Restore scroll position after data loads
  useEffect(() => {
    if (!loading && shouldRestoreScroll.current && scrollPosRef.current > 0) {
      // Use multiple methods to ensure scroll restoration
      const restoreScroll = () => {
        window.scrollTo(0, scrollPosRef.current);
        document.documentElement.scrollTop = scrollPosRef.current;
        document.body.scrollTop = scrollPosRef.current;
      };

      // Try immediately
      restoreScroll();

      // Try with requestAnimationFrame
      requestAnimationFrame(() => {
        restoreScroll();
        shouldRestoreScroll.current = false;
      });

      // Fallback with setTimeout
      setTimeout(restoreScroll, 10);
    }
  }, [loading, data]);

  const loadData = useCallback(async () => {
    const params = new URLSearchParams();
    if (mode === "arb" && arbIdFromUser) {
      params.set("arbId", arbIdFromUser);
      if (filterLotNo.trim()) params.set("lotNo", filterLotNo.trim());
      if (filterAccountNo.trim()) params.set("accountNo", filterAccountNo.trim());
      if (filterRefNo.trim()) params.set("refNo", filterRefNo.trim());
    } else {
      if (filterArbId) params.set("arbId", filterArbId);
      if (filterLotNo.trim()) params.set("lotNo", filterLotNo.trim());
      if (filterAccountNo.trim()) params.set("accountNo", filterAccountNo.trim());
      if (filterRefNo.trim()) params.set("refNo", filterRefNo.trim());
    }

    // Save scroll position and mark for restoration
    scrollPosRef.current = window.scrollY;
    shouldRestoreScroll.current = scrollPosRef.current > 0;

    // Prevent scroll during loading
    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.documentElement.style.scrollBehavior;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.scrollBehavior = 'auto';

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/mis?${params}`);
      const json = await res.json();
      setData(Array.isArray(json) ? json : []);
      setHasFetched(true);
      setCurrentPage(1);
    } catch (e) {
      console.error(e);
      setData([]);
      shouldRestoreScroll.current = false;
    } finally {
      setLoading(false);
      // Restore scroll behavior
      setTimeout(() => {
        document.body.style.overflow = originalOverflow;
        document.documentElement.style.scrollBehavior = originalPosition;
      }, 50);
    }
  }, [mode, arbIdFromUser, filterArbId, filterLotNo, filterAccountNo, filterRefNo]);

  useEffect(() => {
    if (mode === "arb" && arbIdFromUser) {
      shouldRestoreScroll.current = false; // Don't restore on initial load
      loadData();
    }
  }, [mode, arbIdFromUser, loadData]);

  const exportExcel = () => {
    if (!data.length) return;
    const rows = data.map((row) => {
      const base = {
        "Sr#": row.Sr_No,
        "Reference No": row.Reference_No,
        "Customer Name": row.Customer_Name,
        "Account No": row.Account_No,
        "Lot No": row.Lot_No,
      };
      if (mode === "resolution") base["Arbitrator"] = row.Arbitrator;

      // Check if case has been contested (if Contested_Meeting_No exists, the case is contested)
      const caseIsContested = row.Contested_Meeting_No != null;
      const contestedAtMeeting = row.Contested_Meeting_No;
      let contestedReached = false;

      // Add stage columns (excluding Roznama and PURSIS columns)
      STAGES.forEach((s) => {
        // Skip Roznama and PURSIS columns
        if (s.label.includes("Roznama") || s.label === "PURSIS") return;

        // For hearing columns, add contested status
        if (s.key === "First_Hearing" || s.key === "Second_Hearing" || s.key === "Third_Hearing") {
          const meetingNo = s.key === "First_Hearing" ? 1 : s.key === "Second_Hearing" ? 2 : 3;
          const isContested = row.Contested_Meeting_No === meetingNo;
          const hearingDate = row[s.key];

          if (contestedReached) {
            base[s.label] = "N/A";
            base[`${s.label} Status`] = "N/A";
          } else if (hearingDate) {
            base[s.label] = fmt(hearingDate);
            base[`${s.label} Status`] = isContested ? "Contested" : "Not Contested";
            if (isContested) contestedReached = true;
          } else {
            base[s.label] = "Pending";
            base[`${s.label} Status`] = "";
          }
        } else if (s.key === "Award" && contestedReached) {
          // Award column for contested cases — show contest award data
          const hasAward = row.Contest_Status === "award_uploaded" || row.Contest_Status === "done";
          base[s.label] = hasAward ? (fmt(row.Contest_Award_Date) || "Issued") : "Pending";
        } else if (!s.key && s.docKey) {
          if (contestedReached) {
            base[s.label] = "N/A";
          } else {
            base[s.label] = row[s.docKey] ? "Completed" : "Pending";
          }
        } else if (s.key) {
          if (contestedReached) {
            base[s.label] = "N/A";
          } else {
            base[s.label] = fmt(row[s.key]) || "Pending";
          }
        }
      });

      base["Status"] = row.Status;
      return base;
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "MIS");
    XLSX.writeFile(wb, `MIS_${mode}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const totalPages = Math.ceil(data.length / PER_PAGE);
  const pageData = data.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  return (
    <div className="container-fluid">
      <div className="row align-items-center my-3">
        <div className="col">
          <h4 className="mb-0">{mode === "arb" ? "Export Excel" : "MIS Sheet"}</h4>
          <small className="text-muted">
            {mode === "arb"
              ? "Track your assigned cases and their progress"
              : "Management Information System — Case Stage Progress"}
          </small>
        </div>
        <div className="col-auto">
          <button className="btn btn-success btn-sm d-flex align-items-center gap-1" onClick={exportExcel} disabled={!data.length}>
            <FaFileExcel /> Export Excel
          </button>
        </div>
      </div>

      {mode === "resolution" && (
        <div className="row g-2 mb-3">
          <div className="col-md-3">
            <Form.Select value={filterArbId} onChange={(e) => setFilterArbId(e.target.value)} disabled={loading}>
              <option value="">All Arbitrators</option>
              {arbitrators.map((a, i) => (
                <option key={i} value={a.Arb_id || i}>{a.Arb_name}</option>
              ))}
            </Form.Select>
          </div>
          <div className="col-md-2">
            <Form.Control
              type="text"
              placeholder="Lot No"
              value={filterLotNo}
              onChange={(e) => setFilterLotNo(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="col-md-2">
            <Form.Control
              type="text"
              placeholder="Account No"
              value={filterAccountNo}
              onChange={(e) => setFilterAccountNo(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="col-md-2">
            <Form.Control
              type="text"
              placeholder="Reference No"
              value={filterRefNo}
              onChange={(e) => setFilterRefNo(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="col-md-1">
            <button className="btn btn-primary w-100" onClick={loadData} disabled={loading}>
              {loading ? "..." : "Load"}
            </button>
          </div>
          <div className="col-md-1">
            <button
              className="btn btn-outline-secondary w-100"
              onClick={() => {
                setFilterArbId("");
                setFilterLotNo("");
                setFilterAccountNo("");
                setFilterRefNo("");
                setData([]);
                setHasFetched(false);
              }}
              disabled={loading}
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {mode === "arb" && (
        <div className="row g-2 mb-3">
          <div className="col-md-3">
            <Form.Control
              type="text"
              placeholder="Lot No"
              value={filterLotNo}
              onChange={(e) => setFilterLotNo(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="col-md-3">
            <Form.Control
              type="text"
              placeholder="Account No"
              value={filterAccountNo}
              onChange={(e) => setFilterAccountNo(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="col-md-3">
            <Form.Control
              type="text"
              placeholder="Reference No"
              value={filterRefNo}
              onChange={(e) => setFilterRefNo(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="col-md-1">
            <button className="btn btn-primary w-100" onClick={loadData} disabled={loading}>
              {loading ? "..." : "Search"}
            </button>
          </div>
          <div className="col-md-1">
            <button
              className="btn btn-outline-secondary w-100"
              onClick={() => {
                setFilterLotNo("");
                setFilterAccountNo("");
                setFilterRefNo("");
                loadData();
              }}
              disabled={loading}
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted mt-2 mb-0">Loading MIS data...</p>
        </div>
      )}

      {!loading && data.length === 0 && (
        <div className="alert alert-light border text-center">
          {!hasFetched ? "Select filters and click Load MIS." : "No cases found for the selected filters."}
        </div>
      )}

      {!loading && data.length > 0 && (
        <>
          <div className="text-muted mb-2" style={{ fontSize: "0.85rem" }}>
            {data.length} case(s) found
          </div>
          <div className="table-responsive" style={{ fontSize: "0.78rem" }}>
            <table className="table table-bordered table-hover text-center align-middle">
              <thead className="table-dark" style={{ position: "sticky", top: 0 }}>
                <tr>
                  <th>#</th>
                  <th>Reference No</th>
                  <th>Customer</th>
                  <th>Account No</th>
                  <th>Lot</th>
                  {mode === "resolution" && <th>Arbitrator</th>}
                  {STAGES.map((s) => <th key={s.label}>{s.label}</th>)}
                  <th>Status</th>
                  <th>Link</th>
                </tr>
              </thead>
              <tbody>
                {pageData.map((row) => {
                  // Track if we've passed a contested hearing for this row
                  let contestedReached = false;

                  return (
                    <tr key={row.Sr_No}>
                      <td>{row.Sr_No}</td>
                      <td style={{ whiteSpace: "nowrap" }}>{row.Reference_No}</td>
                      <td style={{ textAlign: "left" }}>{row.Customer_Name}</td>
                      <td>{row.Account_No}</td>
                      <td>{row.Lot_No}</td>
                      {mode === "resolution" && <td>{row.Arbitrator}</td>}
                      {STAGES.map((s) => {
                        // Check if this is a hearing column
                        const isHearingColumn = s.key === "First_Hearing" || s.key === "Second_Hearing" || s.key === "Third_Hearing";

                        if (isHearingColumn) {
                          const meetingNo = s.key === "First_Hearing" ? 1 : s.key === "Second_Hearing" ? 2 : 3;
                          const isContested = row.Contested_Meeting_No === meetingNo;
                          const hearingDate = row[s.key];

                          // If already past contested, show N/A
                          if (contestedReached) {
                            return (
                              <td key={s.label}>
                                <span className="text-muted" style={{ fontSize: "0.75rem" }}>N/A</span>
                              </td>
                            );
                          }

                          // Mark contested reached after rendering this column
                          const willMarkContested = isContested;

                          const cell = (
                            <td key={s.label}>
                              {hearingDate ? (
                                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                                  <FaCheckCircle className="text-success" />
                                  <span style={{ fontSize: "0.7rem" }}>{fmt(hearingDate)}</span>
                                  {isContested && (
                                    <span className="badge bg-warning text-dark" style={{ fontSize: "0.6rem", marginTop: "4px" }}>Contested</span>
                                  )}
                                </div>
                              ) : (
                                <FaTimesCircle className="text-secondary opacity-25" />
                              )}
                            </td>
                          );

                          if (willMarkContested) contestedReached = true;
                          return cell;
                        }

                        // Award column for contested cases — show contest award data instead of N/A
                        if (s.key === "Award" && contestedReached) {
                          const hasAward = row.Contest_Status === "award_uploaded" || row.Contest_Status === "done";
                          const awardDoc = row.Contest_Award_Doc;
                          const awardDate = row.Contest_Award_Date;
                          return (
                            <td key={s.label}>
                              {hasAward ? (
                                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                                  <FaCheckCircle className="text-success" />
                                  {awardDate && <span style={{ fontSize: "0.7rem" }}>{fmt(awardDate)}</span>}
                                  {awardDoc && (
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      style={{ padding: "2px 6px", fontSize: "0.7rem" }}
                                      onClick={() => handleDownload(awardDoc.serviceId, awardDoc.serviceTypeId, "Award")}
                                      title="Download Award"
                                    >
                                      <FaFilePdf /> PDF
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <FaTimesCircle className="text-secondary opacity-25" />
                              )}
                            </td>
                          );
                        }

                        // For all other columns, show N/A if contested reached
                        if (contestedReached) {
                          return (
                            <td key={s.label}>
                              <span className="text-muted" style={{ fontSize: "0.75rem" }}>N/A</span>
                            </td>
                          );
                        }

                        // Regular column rendering
                        return (
                          <td key={s.label}>
                            {s.key && s.docKey && row[s.key] ? (
                              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                                <FaCheckCircle className="text-success" />
                                <span style={{ fontSize: "0.7rem" }}>{fmt(row[s.key])}</span>
                                {row[s.docKey] && (
                                  <button
                                    className="btn btn-sm btn-outline-primary"
                                    style={{ padding: "2px 6px", fontSize: "0.7rem" }}
                                    onClick={() => handleDownload(row[s.docKey].serviceId, row[s.docKey].serviceTypeId, s.label)}
                                    title="Download Document"
                                  >
                                    <FaFilePdf /> PDF
                                  </button>
                                )}
                              </div>
                            ) : s.key && !s.docKey && row[s.key] ? (
                              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                                <FaCheckCircle className="text-success" />
                                <span style={{ fontSize: "0.7rem" }}>{fmt(row[s.key])}</span>
                              </div>
                            ) : !s.key && s.docKey && row[s.docKey] ? (
                              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                                <FaCheckCircle className="text-success" />
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  style={{ padding: "2px 6px", fontSize: "0.7rem" }}
                                  onClick={() => handleDownload(row[s.docKey].serviceId, row[s.docKey].serviceTypeId, s.label)}
                                  title="Download Document"
                                >
                                  <FaFilePdf /> PDF
                                </button>
                              </div>
                            ) : (
                              <FaTimesCircle className="text-secondary opacity-25" />
                            )}
                          </td>
                        );
                      })}
                      <td>{statusBadge(row.Status)}</td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <a
                          href={`${window.location.origin}/case-files/${encodeURIComponent(row.Reference_No)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
                          style={{ padding: "2px 8px", fontSize: "0.72rem" }}
                          title="Open file manager for this case"
                        >
                          <FaExternalLinkAlt size={10} /> Files
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <Pagination className="justify-content-center mt-2" size="sm">
              <Pagination.Prev disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} />
              {[...Array(totalPages)].map((_, i) => (
                <Pagination.Item key={i} active={currentPage === i + 1} onClick={() => setCurrentPage(i + 1)}>
                  {i + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)} />
            </Pagination>
          )}
        </>
      )}
    </div>
  );
};

export default MisSheet;
